// src/pages/Admin/Requests/RequestsAdmin.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import useScrollToTop from "./../../../hooks/useScollToTop";
// import { ToastContainer, toast } from "react-toastify";
import Button from "./../../../components/Button/Button";
// import "react-toastify/dist/ReactToastify.css";
import EditRequestModal from "./EditRequestModal";
import AddRequestModal from "./AddRequestModal";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";

// Badges helpers
const priorityBadge = (p) => {
  const map = {
    High: "bg-rose-600",
    Medium: "bg-amber-500",
    Low: "bg-emerald-600",
  };
  const label = p === "High" ? "Urgent" : p === "Medium" ? "Moyenne" : "Basse";
  return { className: map[p] || "bg-slate-500", label };
};

const statusBadge = (s) => {
  const map = {
    Pending: "bg-yellow-500",
    "In Progress": "bg-blue-600",
    Completed: "bg-green-600",
  };
  const label =
    s === "Pending"
      ? "En attente"
      : s === "In Progress"
        ? "Traitement en cours"
        : "Terminée";
  return { className: map[s] || "bg-slate-500", label };
};

export default function RequestsAdmin() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const ASSO_ID = process.env.REACT_APP_ASSO_ID;

  const [requestsData, setRequestsData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentRequest, setCurrentRequest] = useState(null);

  // Filtres
  const [q, setQ] = useState("");
  const [fPriority, setFPriority] = useState("");
  const [fStatus, setFStatus] = useState("");

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `https://api.monappliclub.com/api/requests/RequestsByAssociationId/${ASSO_ID}`,
      );
      setRequestsData(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      // toast.error("Erreur lors de la récupération des requêtes");
    } finally {
      setLoading(false);
    }
  }, [ASSO_ID]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return requestsData.filter((r) => {
      const matchQ =
        !query ||
        `${r.title || ""} ${r.description || ""}`.toLowerCase().includes(query);
      const matchP = !fPriority || r.priority === fPriority;
      const matchS = !fStatus || r.status === fStatus;
      return matchQ && matchP && matchS;
    });
  }, [requestsData, q, fPriority, fStatus]);

  const handleEditClick = (request) => {
    setCurrentRequest(request);
    setShowEditModal(true);
  };

  const handleDeleteClick = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette requête ?"))
      return;
    try {
      await axios.delete(
        `https://api.monappliclub.com/api/requests/deleteRequest/${id}`,
      );
      setRequestsData((prev) => prev.filter((it) => it.id !== id));
      // toast.success("Requête supprimée avec succès !");
    } catch (e) {
      console.error(e);
      // toast.error("Erreur lors de la suppression de la requête.");
    }
  };

  return (
    <section className="w-full">
      {/* Offset pour la navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4">
          {/* Bandeau dégradé : titre + infos + bouton création */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 space-y-4">
              <div className="flex flex-col items-center sm:items-start sm:flex-row sm:justify-between gap-3">
                <div className="text-white text-center sm:text-left">
                  <h3 className="text-lg sm:text-xl font-semibold">
                    Centralisez vos demandes
                  </h3>
                  <p className="text-sm/6 opacity-95 max-w-3xl">
                    Demandez des{" "}
                    <span className="font-semibold">modifications</span>, des{" "}
                    <span className="font-semibold">fonctionnalités</span> ou
                    signalez des <span className="font-semibold">bugs</span>.
                    Selon la <span className="font-semibold">priorité</span>,
                    les requêtes seront traitées au plus vite. Pensez à{" "}
                    <span className="font-semibold">supprimer</span> celles qui
                    sont terminées.
                  </p>
                </div>

                <div className="w-full sm:w-auto flex justify-center sm:justify-end">
                  <Button
                    text="Ajouter une requête"
                    onClick={() => setShowAddModal(true)}
                    className="!bg-white !text-slate-900 hover:!bg-white/90 !rounded-xl !shadow-md"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Barre filtres / recherche */}
          <div className="px-5 py-5 sm:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input
                id="req-search"
                type="text"
                placeholder="Rechercher par titre ou description…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
              <select
                value={fPriority}
                onChange={(e) => setFPriority(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="">Toutes priorités</option>
                <option value="High">Urgent</option>
                <option value="Medium">Moyenne</option>
                <option value="Low">Basse</option>
              </select>
              <select
                value={fStatus}
                onChange={(e) => setFStatus(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="">Tous statuts</option>
                <option value="Pending">En attente</option>
                <option value="In Progress">Traitement en cours</option>
                <option value="Completed">Terminée</option>
              </select>
            </div>
          </div>

          {/* Contenu */}
          <div className="px-5 pb-6 sm:px-8">
            {/* Loading skeleton */}
            {loading && (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-24"
                  />
                ))}
              </div>
            )}
            {/* Empty state */}
            {!loading && filtered.length === 0 && (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucune requête
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Ajoutez une requête ou modifiez vos filtres.
                </p>
              </div>
            )}
            {/* TABLE DESKTOP */}
            {!loading && filtered.length > 0 && (
              <div className="hidden md:block">
                <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                        {[
                          "Titre",
                          "Description",
                          "Priorité",
                          "Statut",
                          "Actions",
                        ].map((h) => (
                          <th
                            key={h}
                            className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                      {filtered.map((request) => {
                        const p = priorityBadge(request.priority);
                        const s = statusBadge(request.status);
                        return (
                          <tr
                            key={request.id}
                            className="group transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                          >
                            <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                              {request.title}
                            </td>
                            <td className="px-4 py-3">
                              <div className="max-h-32 overflow-auto whitespace-pre-wrap break-words pr-1 text-sm leading-snug text-slate-700 dark:text-slate-300">
                                {request.description}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold text-white ${p.className}`}
                              >
                                {p.label}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold text-white ${s.className}`}
                              >
                                {s.label}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="mt-3 flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCurrentRequest(request);
                                    setShowEditModal(true);
                                  }}
                                  className="rounded-lg bg-blue-500 px-3 py-1 text-white"
                                >
                                  Modifier
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handleDeleteClick(request.id)}
                                  className="rounded-lg bg-red-500 px-3 py-1 text-white"
                                >
                                  Supprimer
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {/* CARTES MOBILE */}
            {!loading && filtered.length > 0 && (
              <div className="md:hidden space-y-3">
                {filtered.map((request) => {
                  const p = priorityBadge(request.priority);
                  const s = statusBadge(request.status);
                  return (
                    <div
                      key={`card-${request.id}`}
                      className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-4 shadow-sm transition hover:shadow-md"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                          {request.title}
                        </h3>
                        <div className="flex items-center gap-1">
                          <span
                            className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold text-white ${p.className}`}
                          >
                            {p.label}
                          </span>
                          <span
                            className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold text-white ${s.className}`}
                          >
                            {s.label}
                          </span>
                        </div>
                      </div>

                      <div className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words pr-1 text-sm text-slate-700 dark:text-slate-300">
                        {request.description}
                      </div>

                      <div className="mt-3 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setCurrentRequest(request);
                            setShowEditModal(true);
                          }}
                          className="rounded-lg bg-blue-500 px-3 py-1 text-white"
                        >
                          Modifier
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDeleteClick(request.id)}
                          className="rounded-lg bg-red-500 px-3 py-1 text-white"
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}{" "}
          </div>
        </div>

        {/* <ToastContainer /> */}
      </div>

      {/* Modales */}
      {showEditModal && (
        <EditRequestModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            fetchRequests();
          }}
          request={currentRequest}
          refreshList={fetchRequests}
        />
      )}
      {showAddModal && (
        <AddRequestModal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            fetchRequests();
          }}
          refreshList={fetchRequests}
        />
      )}
    </section>
  );
}
